import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, Text } from "styles/utility-components";
import { z } from "zod";

import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import {
  hasRealRedirectUri,
  type McpClient,
  redirectUriSchema,
} from "lib/hooks/api/auth/mcp/schemas";
import { useUpdateMcpClientRedirectUris } from "lib/hooks/api/auth/mcp/useUpdateMcpClientRedirectUris";
import { CallbackUrlField } from "./CallbackUrlField";

const formSchema = z.object({ redirectUri: redirectUriSchema });

type FormValues = z.infer<typeof formSchema>;

type Props = {
  credential: McpClient;
  onClose: () => void;
};

export function SetCallbackUrlModal({ credential, onClose }: Props) {
  const isFirstTime = !hasRealRedirectUri(credential);
  const currentUri = isFirstTime ? undefined : credential.redirect_uris[0];

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { redirectUri: currentUri ?? "" },
  });

  // Saving clears every user's consent to this client, so re-saving the same URL isn't offered.
  const redirectUri = watch("redirectUri") ?? "";
  const isUnchanged = redirectUri.trim() === currentUri;

  const { mutate: updateRedirectUris, isLoading } = useUpdateMcpClientRedirectUris({
    onSuccess: () => {
      toast.success("Callback URL saved");
      onClose();
    },
    onError: (error) => toast.error(error.message ?? "Could not save the callback URL"),
  });

  return (
    <Modal open onOpenChange={onClose} maxWidth="520px" padding={24}>
      <form
        onSubmit={handleSubmit((values) => {
          if (isUnchanged) return;
          updateRedirectUris({
            clientId: credential.client_id,
            redirectUris: [values.redirectUri],
          });
        })}
      >
        <Flex column gap={24}>
          <Flex column gap={8}>
            <Text h2>{isFirstTime ? "Add callback URL" : "Update callback URL"}</Text>
            <Text description>
              Where your AI tool receives the sign-in response. Until this matches what the tool
              sends, signing in will fail.
            </Text>
          </Flex>

          <CallbackUrlField
            registration={register("redirectUri")}
            value={redirectUri}
            error={errors.redirectUri?.message}
          />

          <Flex justifyContent="flex-end" gap={8}>
            <Button type="button" buttonStyle="inverse" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || isUnchanged} loading={isLoading}>
              Save
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}
